import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Hero from './sections/Hero';
import Stats from './sections/Stats';
import Projects from './sections/Projects';
import Services from './sections/Services';
import Process from './sections/Process';
import Testimonials from './sections/Testimonials';
import FAQ from './sections/FAQ';
import Contact from './sections/Contact';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import FooterSocket from './components/FooterSocket';
import CustomCursor from './components/CustomCursor';
import ScrollProgress from './components/ScrollProgress';
import GetStarted from './pages/GetStarted';
import StartBusiness from './pages/StartBusiness';
import './App.css';

function App() {
  const location = useLocation();
  const hideFooter = location.pathname === '/get-started' || location.pathname === '/get-started/' || location.pathname === '/start-your-business-today' || location.pathname === '/start-your-business-today/';
  const showFooterSocket = location.pathname === '/start-your-business-today' || location.pathname === '/start-your-business-today/';

  // Scroll to top on page load/refresh
  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;
    // Initialize smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';

    let observer;
    let handleScroll;
    const magneticHandlers = [];

    // Use setTimeout to ensure DOM is ready
    const initAnimations = setTimeout(() => {
      try {
        // Enhanced Intersection Observer for multiple animation types
        const observerOptions = {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        };

        observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-in');
            }
          });
        }, observerOptions);

        // Observe all elements with animation classes
        const animatedElements = document.querySelectorAll(
          '.fade-in, .slide-in-left, .slide-in-right, .scale-in, .rotate-in'
        );
        if (animatedElements.length > 0) {
          animatedElements.forEach(el => observer.observe(el));
        }

        // Parallax effect on scroll
        let ticking = false;
        handleScroll = () => {
          if (!ticking) {
            window.requestAnimationFrame(() => {
              const scrolled = window.pageYOffset;
              const parallaxElements = document.querySelectorAll('.parallax');

              if (parallaxElements.length > 0) {
                parallaxElements.forEach((element) => {
                  const speed = element.dataset.speed || 0.5;
                  const yPos = -(scrolled * speed);
                  element.style.transform = `translateY(${yPos}px)`;
                });
              }

              ticking = false;
            });
            ticking = true;
          }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        // Magnetic button effect
        const magneticElements = document.querySelectorAll('.magnetic');

        if (magneticElements.length > 0) {
          magneticElements.forEach(element => {
            const handleMouseMove = (e) => {
              const pos = element.getBoundingClientRect();
              const mx = e.clientX - pos.left - pos.width / 2;
              const my = e.clientY - pos.top - pos.height / 2;

              element.style.transform = `translate(${mx * 0.15}px, ${my * 0.15}px)`;
            };

            const handleMouseLeave = () => {
              element.style.transform = 'translate(0px, 0px)';
            };

            element.addEventListener('mousemove', handleMouseMove, { passive: true });
            element.addEventListener('mouseleave', handleMouseLeave);
            
            magneticHandlers.push({ element, handleMouseMove, handleMouseLeave });
          });
        }
      } catch (error) {
        console.error('Error initializing animations:', error);
      }
    }, 100);

    return () => {
      clearTimeout(initAnimations);
      if (observer) {
        observer.disconnect();
      }
      if (handleScroll) {
        window.removeEventListener('scroll', handleScroll);
      }
      magneticHandlers.forEach(({ element, handleMouseMove, handleMouseLeave }) => {
        element.removeEventListener('mousemove', handleMouseMove);
        element.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, [location.pathname]);

  return (
    <div className="App">
      <ScrollProgress />
      <CustomCursor />
      <Navigation />
      <main>
        <Routes>
          <Route path="/" element={
            <>
              <Hero />
              <Stats />
              <Projects />
              <Services />
              <Process />
              <Testimonials />
              <FAQ />
              <Contact />
            </>
          } />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/get-started/" element={<GetStarted />} />
          <Route path="/start-your-business-today" element={<StartBusiness />} />
          <Route path="/start-your-business-today/" element={<StartBusiness />} />
        </Routes>
      </main>
      {!hideFooter && <Footer />}
      {showFooterSocket && <FooterSocket />}
    </div>
  );
}

export default App;
